import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { AlertCircle, Check, ChevronDown, ChevronUp, Clipboard, Download, Film, FolderInput, Heart, Repeat2, Trash2, X } from "lucide-react";
import {
  buildDownloadFilename,
  favoriteJob,
  presignAsset,
  swrFetcher,
  unfavoriteJob,
  type JobInfo,
  type ProjectInfo,
} from "@/lib/api";
import { projectName } from "@/lib/projectMove";
import {
  jobMetaRows,
  jobReferences,
  jobSpecRows,
  labelForSlot,
  promptOf,
  type ReferenceValue,
} from "@/lib/jobDetails";
import { useAssetUrl } from "@/hooks/useAssetUrl";
import { useDownloadAsset } from "@/hooks/useDownloadAsset";
import { GenerationStage, type StagePosition } from "@/components/GenerationStage";
import { toggleElementFullscreen } from "@/lib/mediaFullscreen";
import { viewerKeyAction } from "@/lib/viewerNav";
import { AssetThumb } from "@/components/media/AssetThumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const PROMPT_FOLD_LENGTH = 420;
/** Icon-sized until `.viewer-actions` has room for the labels (see globals.css). */
const ACTION_CLASS = "min-w-11 shrink-0 gap-1.5 px-2.5 sm:min-w-9";

interface Props {
  job: JobInfo | null;
  open: boolean;
  onClose: () => void;
  onContinue?: (job: JobInfo) => void;
  onRecreate?: () => void;
  /** Opens the move-to-project picker; shown only for generations the viewer manages. */
  onMove?: () => void;
  /** Shows the heart for completed generations the viewer manages; fired after the server toggled it. */
  onFavoriteChange?: (job: JobInfo, favorited: boolean) => void;
  /** Asks to move the generation to Trash; the viewer stays open and steps on once it leaves the list. */
  onTrash?: () => void;
  /** Place in the list being browsed; arrows, swipe and ←/→ step through it. */
  position?: StagePosition | null;
  onPrev?: () => void;
  onNext?: () => void;
  loadingNext?: boolean;
}

type FullscreenMode = "off" | "native" | "css";
/** An Escape that just left native fullscreen must not also close the dialog. */
const ESCAPE_GRACE_MS = 400;

function RailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-studio-border px-4 py-4">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.16em] text-studio-faint">{title}</h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function ReferenceChip({ label, reference }: { label: string; reference: ReferenceValue }) {
  const typeLabel = reference.type ?? label.toLowerCase();
  return (
    <div className="flex items-center gap-2 rounded-xl border border-studio-border bg-studio-card px-2 py-1.5">
      <span className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-studio-panel">
        <AssetThumb
          type={(reference.type as "video" | "image" | "audio") ?? "image"}
          storageKey={reference.thumbnail_storage_key ?? reference.storage_key}
          assetId={reference.asset_id}
        />
      </span>
      <span className="min-w-0">
        <span className="block text-[11px] font-medium text-studio-ink">{label}</span>
        <span className="block truncate text-[10px] text-studio-muted">{reference.mime ?? typeLabel}</span>
      </span>
    </div>
  );
}

export function JobDetailDialog({
  job,
  open,
  onClose,
  onContinue,
  onRecreate,
  onMove,
  onFavoriteChange,
  onTrash,
  position,
  onPrev,
  onNext,
  loadingNext,
}: Props) {
  const { data: projects } = useSWR<ProjectInfo[]>(open ? "/api/projects?include_archived=false" : null, swrFetcher);
  const [copied, setCopied] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const { download, downloading, failed: downloadError, reset: resetDownload } = useDownloadAsset();
  const asset = job?.result.assets?.[0];
  const { url: mediaUrl } = useAssetUrl(open ? asset?.storage_key : null);
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState<FullscreenMode>("off");
  const fullscreenRef = useRef<FullscreenMode>("off");
  fullscreenRef.current = fullscreen;
  const nativeExitAt = useRef(0);
  const canFullscreen = asset?.type === "image" || asset?.type === "video";
  const [favorited, setFavorited] = useState(Boolean(job?.favorited_at));
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  // The server value wins whenever the viewed item changes or the list refetches.
  useEffect(() => {
    setFavorited(Boolean(job?.favorited_at));
  }, [job?.id, job?.favorited_at]);

  useEffect(() => {
    if (!open) {
      setPromptExpanded(false);
      resetDownload();
    }
  }, [open, resetDownload]);

  // Each step starts from a folded prompt and a clean download state.
  useEffect(() => {
    setPromptExpanded(false);
    setCopied(false);
    resetDownload();
  }, [job?.id, resetDownload]);

  useEffect(() => {
    const onChange = () => {
      if (document.fullscreenElement === stageRef.current && stageRef.current) {
        setFullscreen("native");
      } else if (fullscreenRef.current === "native") {
        nativeExitAt.current = Date.now();
        setFullscreen("off");
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Closing the viewer always leaves fullscreen.
  useEffect(() => {
    if (open) return;
    if (stageRef.current && document.fullscreenElement === stageRef.current) void document.exitFullscreen().catch(() => undefined);
    setFullscreen("off");
  }, [open]);

  const toggleFullscreen = useCallback(() => {
    const stage = stageRef.current;
    const mode = fullscreenRef.current;
    if (mode === "css") {
      setFullscreen("off");
      return;
    }
    if (!stage) return;
    if (mode === "native" && document.fullscreenElement !== stage) {
      // The browser already left fullscreen (its own Escape handling).
      nativeExitAt.current = Date.now();
      setFullscreen("off");
      return;
    }
    // requestFullscreen runs synchronously inside the tap; iPhone Safari falls back to CSS.
    void toggleElementFullscreen(stage, document).then((honoured) => {
      if (!honoured && fullscreenRef.current === "off") setFullscreen("css");
    });
  }, []);

  const handleClose = useCallback(() => {
    if (fullscreenRef.current !== "off") {
      toggleFullscreen();
      return;
    }
    if (Date.now() - nativeExitAt.current < ESCAPE_GRACE_MS) return;
    onClose();
  }, [onClose, toggleFullscreen]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as Node | null;
      if (target !== document.body && !(target && rootRef.current?.contains(target))) return;
      const action = viewerKeyAction(event);
      if (action === "prev" && onPrev) onPrev();
      else if (action === "next" && onNext) onNext();
      else if (action === "fullscreen" && canFullscreen) toggleFullscreen();
      else return;
      event.preventDefault();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onPrev, onNext, canFullscreen, toggleFullscreen]);

  if (!job) return null;

  const prompt = promptOf(job);
  const resolved = job.params.creative_direction_resolved;
  const direction = resolved && typeof resolved === "object" ? (resolved as Record<string, unknown>) : null;
  const directionSlots = Array.isArray(direction?.slots) ? direction.slots : [];
  const bundle =
    direction?.bundle && typeof direction.bundle === "object"
      ? (direction.bundle as Record<string, unknown>)
      : null;
  const specRows = jobSpecRows(job, asset);
  const metaRows = jobMetaRows(job);
  const references = jobReferences(job.params);
  const foldable = prompt.length > PROMPT_FOLD_LENGTH;
  const hasDirection =
    direction && (directionSlots.length > 0 || typeof direction.genre === "string" || typeof bundle?.title === "string");

  async function copyPrompt() {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard permission denied */
    }
  }

  async function toggleFavorite() {
    if (!job || favoriteBusy) return;
    const next = !favorited;
    setFavorited(next);
    setFavoriteBusy(true);
    try {
      await (next ? favoriteJob(job.id) : unfavoriteJob(job.id));
      onFavoriteChange?.(job, next);
    } catch {
      setFavorited(!next);
    } finally {
      setFavoriteBusy(false);
    }
  }

  async function handleDownload() {
    if (!asset || downloading) return;
    const filename = buildDownloadFilename(job.model_key, job.id, 0, asset.mime_type);
    await download({ filename, resolveUrl: () => presignAsset(asset.storage_key, { download: true, filename }) });
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      ariaLabel={`${job.model_key} generation details`}
      className="h-[92dvh] max-h-[92dvh] w-full max-w-7xl bg-studio-panel sm:h-[88vh] sm:max-h-[88vh]"
      fullBleed={fullscreen === "css"}
    >
      <div ref={rootRef} className="flex min-h-0 flex-1 flex-col lg:flex-row">

        <aside
          className={cn(
            "flex min-h-0 w-full flex-1 flex-col border-t border-studio-border lg:w-[400px] lg:flex-none xl:w-[520px] lg:shrink-0 lg:border-l lg:border-t-0",
            fullscreen !== "off" && "hidden lg:hidden"
          )}
        >
          <header className="flex shrink-0 items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-studio-ink">{job.model_key}</p>
              <p className="mt-0.5 text-[11px] text-studio-muted">{job.capability_key}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={job.state === "completed" ? "success" : job.state === "failed" ? "danger" : "neutral"}>
                {job.state}
              </Badge>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <RailSection title="Prompt">
              <p
                className={
                  foldable && !promptExpanded
                    ? "line-clamp-[8] whitespace-pre-wrap break-words text-xs leading-5 text-studio-ink"
                    : "whitespace-pre-wrap break-words text-xs leading-5 text-studio-ink"
                }
              >
                {prompt || "—"}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => void copyPrompt()} disabled={!prompt}>
                  {copied ? <Check className="h-3.5 w-3.5 text-studio-success" /> : <Clipboard className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                {foldable && (
                  <Button variant="ghost" size="sm" onClick={() => setPromptExpanded((value) => !value)}>
                    {promptExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {promptExpanded ? "Show less" : "Show more"}
                  </Button>
                )}
              </div>
            </RailSection>

            <RailSection title="Details">
              <dl className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {[...specRows, ...metaRows, { label: "Project", value: projectName(projects, job.project_id ?? null) }].map((row) => (
                  <div key={row.label} className="min-w-0">
                    <dt className="text-[10px] uppercase tracking-wide text-studio-faint">{row.label}</dt>
                    <dd className="mt-0.5 break-words text-xs tabular-nums text-studio-ink">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </RailSection>

            {references.length > 0 && (
              <RailSection title="References">
                <div className="grid gap-2">
                  {references.map(({ label, reference }, index) => (
                    <ReferenceChip key={`${label}-${reference.asset_id ?? index}`} label={label} reference={reference} />
                  ))}
                </div>
              </RailSection>
            )}

            {hasDirection && (
              <RailSection title="Creative direction">
                <div className="flex flex-wrap gap-2">
                  {typeof direction.genre === "string" && direction.genre && (
                    <Badge variant="accent">Genre: {direction.genre}</Badge>
                  )}
                  {typeof bundle?.title === "string" && bundle.title && <Badge>Bundle: {bundle.title}</Badge>}
                </div>
                {directionSlots.length > 0 && (
                  <div className="mt-3 divide-y divide-studio-border">
                    {directionSlots.map((raw, index) => {
                      if (!raw || typeof raw !== "object") return null;
                      const row = raw as Record<string, unknown>;
                      const title =
                        (typeof row.title === "string" && row.title) ||
                        (typeof row.stable_key === "string" && row.stable_key) ||
                        "Selected";
                      return (
                        <div
                          key={`${String(row.slot)}-${index}`}
                          className="flex items-center justify-between gap-4 py-2 text-xs"
                        >
                          <span className="text-studio-muted">{labelForSlot(row.slot)}</span>
                          <span className="text-right font-medium text-studio-ink">{title}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </RailSection>
            )}
          </div>

          {/* One row at every width: secondary actions drop to icons until the row fits their labels. */}
          <div className="viewer-actions flex shrink-0 items-center gap-1.5 border-t border-studio-border px-4 py-3">
            {asset && mediaUrl && (
              <Button
                variant={downloadError ? "danger" : "secondary"}
                size="sm"
                aria-label={downloadError ? "Retry download" : "Download"}
                title={downloadError ? "Retry download" : "Download"}
                className={ACTION_CLASS}
                onClick={() => void handleDownload()}
                loading={downloading}
              >
                {!downloading && (downloadError ? <AlertCircle className="h-4 w-4" /> : <Download className="h-4 w-4" />)}
                <span className="viewer-action-label sr-only">{downloadError ? "Retry" : "Download"}</span>
              </Button>
            )}
            {onContinue && job.result.interaction_id && (
              <Button
                variant="secondary"
                size="sm"
                aria-label="Continue"
                title="Continue editing in this thread"
                className={ACTION_CLASS}
                onClick={() => {
                  onContinue(job);
                  onClose();
                }}
              >
                <Film className="h-4 w-4" />
                <span className="viewer-action-label sr-only">Continue</span>
              </Button>
            )}
            {onMove && (
              <Button
                variant="secondary"
                size="sm"
                aria-label="Move"
                title="Move to project"
                className={ACTION_CLASS}
                onClick={() => {
                  onClose();
                  onMove();
                }}
              >
                <FolderInput className="h-4 w-4" />
                <span className="viewer-action-label sr-only">Move</span>
              </Button>
            )}
            {onFavoriteChange && job.can_manage && job.state === "completed" && (
              <Button
                variant="secondary"
                size="sm"
                aria-label="Like"
                aria-pressed={favorited}
                title={favorited ? "Remove from favorites" : "Add to favorites"}
                disabled={favoriteBusy}
                className={ACTION_CLASS}
                onClick={() => void toggleFavorite()}
              >
                <Heart className={cn("h-4 w-4", favorited && "fill-current text-studio-danger")} />
                <span className="viewer-action-label sr-only">{favorited ? "Liked" : "Like"}</span>
              </Button>
            )}
            {onTrash && (
              <Button
                variant="secondary"
                size="sm"
                aria-label="Delete"
                title="Move to Trash"
                className={cn(ACTION_CLASS, "hover:border-studio-danger/50 hover:text-studio-danger")}
                onClick={onTrash}
              >
                <Trash2 className="h-4 w-4" />
                <span className="viewer-action-label sr-only">Delete</span>
              </Button>
            )}
            {onRecreate && (
              <Button size="sm" className="ml-auto min-w-0 gap-1.5 px-2.5" onClick={onRecreate}>
                <Repeat2 className="h-4 w-4 max-[359px]:hidden" /> Recreate
              </Button>
            )}
            <span role="status" aria-live="polite" className="sr-only">
              {downloadError ? "Download failed. Please try again." : ""}
            </span>
          </div>
        </aside>

        {/* After the rail in the DOM so dialog focus starts on Close; shown first via `order-first`. */}
        <GenerationStage
          ref={stageRef}
          mediaKey={job.id}
          type={asset?.type ?? "image"}
          url={mediaUrl}
          alt={prompt || "Generation"}
          position={position}
          onPrev={onPrev}
          onNext={onNext}
          loadingNext={loadingNext}
          fullscreen={fullscreen !== "off"}
          onToggleFullscreen={toggleFullscreen}
        />
      </div>
    </Dialog>
  );
}
